/**
 * Filterlogik für den Forderungsmanager (Issues #600 B3, #689).
 *
 * tageSeit verwendet die Fallback-Kette:
 *   faellig_am → mietende → rechnung_datum → created_at
 *
 * Dadurch filtern Alters-Bucket-Filter nach tatsächlichen Überfälligkeits-
 * tagen statt nach dem Erstellungsdatum.
 */

import type { Forderungsakte } from '../hooks/useForderungen';

export const ALTERS_BUCKET_DEFS = [
  { key: '<14d',  label: '<14d',   minTage: 0,  maxTage: 13 },
  { key: '14-30', label: '14–30',  minTage: 14, maxTage: 30 },
  { key: '30-60', label: '30–60',  minTage: 31, maxTage: 60 },
  { key: '60-90', label: '60–90',  minTage: 61, maxTage: 90 },
  { key: '>90',   label: '>90',    minTage: 91, maxTage: Infinity },
] as const;

export type AltersBucketKey = (typeof ALTERS_BUCKET_DEFS)[number]['key'];

/** Legacy 4-bucket type for backward compatibility (Issue #689). */
export type AltersBucket = '0-30' | '31-60' | '61-90' | '>90';

// Mapping: chip id → forderungsakten.typ CHECK constraint values ('mietfahrzeug' | 'werkstatt_auslage')
export const AKTEN_TYP_FILTER_LIST = [
  { id: 'mietwagen', label: 'Mietwagen',  typ: 'mietfahrzeug' },
  { id: 'werkstatt', label: 'Werkstatt',  typ: 'werkstatt_auslage' },
] as const;

export type AktenTypId = (typeof AKTEN_TYP_FILTER_LIST)[number]['id'];

export interface ForderungenFilterState {
  stages: number[];
  aktenTypen: AktenTypId[];
  versicherung: string;
  betragsMin: number;
  betragsMax: number;
  altersBuckets: AltersBucketKey[];
}

/** Visual maximum shown on the amount-range slider. The default filter uses Infinity so claims above this remain visible. */
export const BETRAG_SLIDER_MAX = 10000;

export const DEFAULT_FILTER_STATE: ForderungenFilterState = {
  stages: [],
  aktenTypen: [],
  versicherung: '',
  betragsMin: 0,
  betragsMax: Infinity,
  altersBuckets: [],
};

export const LOCALSTORAGE_KEY = 'forderungsmanager_filter_v2';

/** Tage seit dem effektiven Fälligkeitsdatum (positiv = überfällig, negativ = noch nicht fällig). */
export function tageSeit(f: Forderungsakte, now: Date = new Date()): number {
  const anchor =
    f.faellig_am ??
    f.mietende ??
    f.rechnung_datum ??
    f.created_at;

  const anchorMs = new Date(anchor).setHours(0, 0, 0, 0);
  const nowMs = new Date(now).setHours(0, 0, 0, 0);
  return Math.round((nowMs - anchorMs) / (1000 * 60 * 60 * 24));
}

/** Filtert eine Liste von Forderungsakten nach Alters-Bucket (4-bucket API, Issue #689). */
export function filterByAltersBucket(
  forderungen: readonly Forderungsakte[],
  bucket: AltersBucket | null | undefined,
  now: Date = new Date(),
): Forderungsakte[] {
  if (!bucket) return [...forderungen];

  return forderungen.filter((f) => {
    const tage = tageSeit(f, now);
    switch (bucket) {
      case '0-30':  return tage >= 0 && tage <= 30;
      case '31-60': return tage >= 31 && tage <= 60;
      case '61-90': return tage >= 61 && tage <= 90;
      case '>90':   return tage > 90;
    }
  });
}

export function applyForderungenFilter(
  data: Forderungsakte[],
  filters: ForderungenFilterState,
  now: Date = new Date(),
): Forderungsakte[] {
  let list = data;

  if (filters.stages.length > 0) {
    list = list.filter((f) => filters.stages.includes(f.stage ?? 0));
  }

  if (filters.aktenTypen.length > 0) {
    const typen: string[] = filters.aktenTypen.map((id) => {
      const def = AKTEN_TYP_FILTER_LIST.find((t) => t.id === id);
      return def?.typ ?? id;
    });
    list = list.filter((f) => typen.includes(f.typ));
  }

  if (filters.versicherung.trim()) {
    const q = filters.versicherung.toLowerCase();
    list = list.filter((f) => f.versicherung_gegner?.toLowerCase().includes(q));
  }

  if (filters.betragsMin > 0 || filters.betragsMax < Infinity) {
    list = list.filter((f) => {
      const betrag = f.gesamtbetrag ?? 0;
      return betrag >= filters.betragsMin && betrag <= filters.betragsMax;
    });
  }

  if (filters.altersBuckets.length > 0) {
    list = list.filter((f) => {
      const tage = tageSeit(f, now);
      return filters.altersBuckets.some((key) => {
        const def = ALTERS_BUCKET_DEFS.find((b) => b.key === key);
        if (!def) return false;
        return tage >= def.minTage && (def.maxTage === Infinity ? true : tage <= def.maxTage);
      });
    });
  }

  return list;
}

export function distinctVersicherungen(data: Forderungsakte[]): string[] {
  const seen = new Set<string>();
  for (const f of data) {
    if (f.versicherung_gegner) seen.add(f.versicherung_gegner);
  }
  return Array.from(seen).sort((a, b) => a.localeCompare(b, 'de'));
}
